import * as React from "react";
import { useCallback } from "react";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { Button } from "@/lib/shadcn/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "@/lib/shadcn/ui/dropdown-menu";
import { cn } from "@/lib/shadcn/utils";
import { Input } from "@/lib/shadcn/ui/input";
import { Tooltip, TooltipProvider, TooltipTrigger } from "@/lib/shadcn/ui/tooltip";
import { TooltipContent } from "../ui/tooltip";

const MENU_NAVIGATION_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End", "Escape", "Tab"]);

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    onChange: (values: string[]) => void;
    placeholder: string;
    selectedValues?: string[];
    variant?: "default" | "destructive" | "outline" | "secondary" | "volt" | "ghost" | "link";
    className?: string;
    width?: string;
    staticText?: string;
    disabled?: boolean;
    /** Accessible name for the in-dropdown search box (placeholders aren't a reliable name). */
    searchLabel?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    onChange,
    placeholder,
    variant = "outline",
    className,
    selectedValues = [],
    staticText,
    disabled = false,
    searchLabel = "Search options",
}) => {
    const [searchQuery, setSearchQuery] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!open) {
            setSearchQuery("");
        }
    }, [open]);

    const getDisplayText = () => {
        if (selectedValues.length === 0) return placeholder;

        const selectedLabels = selectedValues.map((value) => options.find((opt) => opt.value === value)?.label).filter(Boolean);

        const displayCount = 2;
        if (selectedLabels.length <= displayCount) {
            return selectedLabels.join(", ");
        }

        return `${selectedLabels.slice(0, displayCount).join(", ")} +${selectedLabels.length - displayCount} more`;
    };

    const handleCheckedChange = (value: string, checked: boolean) => {
        const newSelected = checked ? [...selectedValues, value] : selectedValues.filter((item) => item !== value);
        onChange(newSelected);
    };

    const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleInputClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
    };

    // Radix autofocuses this input when the menu opens, and the menu's own
    // keydown handler implements roving focus (arrows) and typeahead. Swallowing
    // every key would strand keyboard users in the search box with no way to
    // reach an option; letting every key through would hand printable characters
    // to typeahead instead of the search query. So: navigation keys propagate,
    // everything else stays here.
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!MENU_NAVIGATION_KEYS.has(e.key)) {
            e.stopPropagation();
        }
    };

    const getTooltipText = useCallback(() => {
        if (selectedValues.length === 0) return placeholder;
        return selectedValues
            .map((value) => options.find((opt) => opt.value === value)?.label)
            .filter(Boolean)
            .join(", ");
    }, [selectedValues]);

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                            <Button variant={variant} className={cn("w-full justify-between overflow-hidden", className)} disabled={disabled}>
                                <span className="truncate w-[90%]">{staticText || getDisplayText()}</span>
                                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="max-w-xs wrap-break-word">{getTooltipText()}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent className={cn("w-(--radix-dropdown-menu-trigger-width)")} onClick={(e) => e.stopPropagation()}>
                <div className="p-2">
                    <Input
                        ref={inputRef}
                        placeholder="Search"
                        aria-label={searchLabel}
                        value={searchQuery}
                        onChange={handleInputChange}
                        onClick={handleInputClick}
                        onKeyDown={handleInputKeyDown}
                        className="h-8"
                        disabled={disabled}
                    />
                </div>
                <div className="overflow-y-auto max-h-[200px]">
                    {filteredOptions.length === 0 ? (
                        <div className="text-sm text-muted-foreground p-2">No options found</div>
                    ) : (
                        filteredOptions.map((option) => (
                            <DropdownMenuCheckboxItem
                                key={option.value}
                                checked={selectedValues.includes(option.value)}
                                onCheckedChange={(checked) => handleCheckedChange(option.value, checked)}
                                onSelect={(e) => e.preventDefault()}
                            >
                                {option.label}
                            </DropdownMenuCheckboxItem>
                        ))
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
